import { Box } from '@jfs/ui-new'
import { useNavigation } from '@react-navigation/native'
import { useEffect, useState } from 'react'
import { RefreshControl as RefreshControlMobile, ScrollView } from 'react-native'
import { RefreshControl as RefreshControlMobileWeb } from 'react-native-web-refresh-control'
import { ImageWithInfoSerializer } from '../../comps/ImageWithInfoSerializer/ImageWithInfoSerializer'
import { ListWithDateRange } from '../../comps/ListWithDateRange'
import { ShimmerView } from '../../comps/ShimmerView'
import { actionMenuStrings, settlementScreenString } from '../../data/static/strings'
import { useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { INFINITE_SCROLL_WINDOW, INFINITE_SCROLL_WINDOW_INDEX } from '../../util/common'
import { getErrorState, getLoadingState, getPrevDate, getSelectedDate, isOsMobile } from '../../util/util'

const type = 'settlements'
const Settled = ({ fetchStatsApi, settlementApiHandler }: any) => {
  const navigation = useNavigation()
  const [isLoadingMore, setLoadMore] = useState(false)

  const {
    mid,
    fetchSettlementData,
    resetPaginationSettle,
    resetPaginationTxn,
    moreTxnAvailable,
    selectedDateInfo,
    settlementsLength,
    updateSelectedDateInfo,
    isMaxDateRangeSelected,
    resetSettlementFilterDate,
  } = useStores((store) => ({
    mid: store.user.selectedMid,
    fetchSettlementData: store.settlements.fetchSettlementData,
    resetPaginationSettle: store.settlements.resetPagination,
    resetPaginationTxn: store.transactions.resetPagination,
    moreTxnAvailable: store.moreTxnAvailable,
    selectedDateInfo: store.settlements.selectedDateInfo,
    settlementsLength: store?.settlements?.settlement?.length,
    updateSelectedDateInfo: store.settlements.updateSelectedDateInfo,
    isMaxDateRangeSelected: store.isMaxDateRangeSelected,
    resetSettlementFilterDate: store.settlements.resetSettlementFilterDate,
  }))

  const canFetchMoreTxns = moreTxnAvailable(Routes.SETTLEMENTS)
  const listData = fetchSettlementData(true)

  const loadingState = getLoadingState(settlementApiHandler)
  const errorState = getErrorState(settlementApiHandler)
  const showEmptyImage = listData?.length === 0 && !loadingState && !errorState

  useEffect(() => {
    if (mid) {
      resetSettlementFilterDate()
      const resettedSelectedDateInfo = {
        ...selectedDateInfo,
        fromDate: getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX),
        toDate: new Date(Date.now()),
      }
      fetchStatsApi(mid, type, resettedSelectedDateInfo, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid])

  useEffect(() => {
    if (!loadingState && isLoadingMore) {
      setLoadMore(false)
    }
  }, [isLoadingMore, loadingState])

  const filterDateSelected = selectedDateInfo?.fromDate && selectedDateInfo?.toDate
  const viewOlderRecords =
    !isMaxDateRangeSelected(selectedDateInfo) && selectedDateInfo?.dateLabel === actionMenuStrings.infiniteScrollRange

  const loadPrevWindowRecords = () => {
    fetchStatsApi(
      mid,
      type,
      {
        fromDate: new Date(getPrevDate(new Date(selectedDateInfo?.fromDate), INFINITE_SCROLL_WINDOW)),
        toDate: new Date(getPrevDate(new Date(selectedDateInfo?.toDate), INFINITE_SCROLL_WINDOW)),
        dateLabel: actionMenuStrings.infiniteScrollRange,
      },
      false,
    )

    updateSelectedDateInfo({
      fromDate: new Date(getPrevDate(new Date(selectedDateInfo?.fromDate), INFINITE_SCROLL_WINDOW)),
      toDate: new Date(getPrevDate(new Date(selectedDateInfo?.toDate), INFINITE_SCROLL_WINDOW)),
      dateLabel: actionMenuStrings.infiniteScrollRange,
    })
  }

  const onLoadMore = () => {
    setLoadMore(true)
    const fromDate = new Date(getPrevDate(new Date(selectedDateInfo?.fromDate), INFINITE_SCROLL_WINDOW + 1))
    const toDate = new Date(getPrevDate(new Date(selectedDateInfo?.toDate), INFINITE_SCROLL_WINDOW + 1))
    const newSelectedInfo = {
      fromDate,
      toDate,
      dateLabel: actionMenuStrings.infiniteScrollRange,
    }
    updateSelectedDateInfo(newSelectedInfo)
    if (mid) {
      fetchStatsApi(mid, type, newSelectedInfo, false, false, true)
    }
  }

  const onRefresh = () => {
    resetSettlementFilterDate()
    const resettedSelectedDateInfo = {
      ...selectedDateInfo,
      fromDate: getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX),
      toDate: new Date(Date.now()),
    }
    resetPaginationSettle()
    resetPaginationTxn()
    fetchStatsApi(mid, type, resettedSelectedDateInfo)
  }

  const navigateToAddBank = () => {
    navigation.navigate(Routes.ADD_BANK)
  }

  const getEmptyMessage = (): string => {
    if (!loadingState) {
      return listData.length === 0 ? settlementScreenString.noRecords : settlementScreenString.empty
    }
    return ''
  }

  const handleRetry = (): void => {
    resetSettlementFilterDate()
    const resettedSelectedDateInfo = {
      ...selectedDateInfo,
      fromDate: getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX),
      toDate: new Date(Date.now()),
    }
    resetPaginationSettle()
    resetPaginationTxn()
    fetchStatsApi(mid, type, resettedSelectedDateInfo, false)
  }

  if (loadingState && !isLoadingMore) {
    return <ShimmerView type={Routes.TRANSACTIONS} />
  }

  const RefreshControl = isOsMobile() ? RefreshControlMobile : RefreshControlMobileWeb
  const isEmptyView = showEmptyImage || (errorState && listData.length === 0)
  const handleOnPress = (item) => {
    navigation.navigate(Routes.SETTLEMENT_DETAILS as never, { settlementDatails: item } as never)
  }

  const emptyView = () => {
    return (
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}
        refreshControl={<RefreshControl refreshing={false} onRefresh={onRefresh} />}>
        <ImageWithInfoSerializer
          show={showEmptyImage}
          screenName={Routes.SETTLEMENTS}
          actionButtonVisible={viewOlderRecords}
          // actionButtonVisible={true}
          actionButtonName={'View older Records'}
          onPress={loadPrevWindowRecords}
        />
      </ScrollView>
    )
  }

  return (
    <Box
      f={1}
      overflow={isOsMobile() ? undefined : 'auto'}
      bg={'WHITE'}
      align={isEmptyView || errorState ? 'center' : undefined}
      justify={isEmptyView || errorState ? 'center' : 'flex-start'}>
      <ImageWithInfoSerializer
        show={errorState}
        screenName={'errorTxn'}
        actionButtonVisible={true}
        actionButtonName={'Try Again'}
        onPress={handleRetry}
      />
      <ListWithDateRange
        data={listData}
        refreshControl={<RefreshControl refreshing={false} onRefresh={onRefresh} />}
        onListFooterPress={onLoadMore}
        canFetchMoreTxns={false}
        path={Routes.SETTLEMENTS}
        loadingState={isLoadingMore}
        showShimmerView={isLoadingMore}
        hide={errorState || (loadingState && !isLoadingMore)}
        listEmptyComponent={emptyView}
        handleItemPress={handleOnPress}
        onLoadMore={() => {}}
        isSeeAllVisible={false}
      />
    </Box>
  )
}
export { Settled }
